import { VonqChannelDetails } from 'api-clients/monolith/models/VonqChannelDetails';
import { useMemo } from 'react';

import { buildChannelDefaults } from '@/components/pages/job-posting/utils/channel-defaults';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useChannelType } from '@/hooks/use-channel-type';

import { useOpeningsSelectedForAnotherProduct } from '../use-openings-selected-for-another-product';
import { useUnsupportedOpenings } from '../use-unsupported-openings';
import { CardLayout } from './card-layout';
import { MutedMessage } from './muted-message';

interface VonqChannelCardProps {
  channel: VonqChannelDetails;
  isFree?: boolean;
}

export function VonqChannelCard({
  channel,
  isFree = false,
}: VonqChannelCardProps) {
  const channelType = useChannelType(channel.channel.type);
  const { hookForm } = useJobPostingContext();
  const { getValues, setValue } = hookForm;

  // The form-state key for a Vonq channel is the product ID, because the same Vonq channel
  // (e.g. Monster) can have multiple products selected at once. The real channel slug lives
  // separately on `ChannelFormState.slug` and is built from `channel.channel.slug`.
  const formKey = channel.product_id;
  const channelSlug = channel.channel.slug;
  const displayTitle = channel.title ?? channel.channel.name;
  const rawDescription = channel.description;
  const { allOpeningsUnsupported, message: unsupportedMessage } =
    useUnsupportedOpenings(channel.unsupported_openings, channel.channel.name);

  const {
    openingsSelectedForAnotherProduct,
    isAllOpeningsSelectedForAnotherProduct,
    message: anotherProductMessage,
  } = useOpeningsSelectedForAnotherProduct(
    formKey,
    channelSlug,
    channel.unsupported_openings,
  );

  const description = useMemo(() => {
    if (unsupportedMessage) {
      return <MutedMessage>{unsupportedMessage}</MutedMessage>;
    }

    if (anotherProductMessage) {
      return <MutedMessage>{anotherProductMessage}</MutedMessage>;
    }

    return rawDescription;
  }, [unsupportedMessage, anotherProductMessage, rawDescription]);

  /* Add the channel to the form state with the Vonq product details */
  const handleSelect = () => {
    if (allOpeningsUnsupported) return;

    // Return if this exact product is already selected. We use key on `formKey` (product ID) so
    // multiple products of the same Vonq channel can coexist as separate form-state entries.
    if (Object.keys(getValues('channels') ?? {}).includes(formKey)) return;

    const channels = getValues('channels') ?? {};
    setValue('channels', {
      ...channels,
      [formKey]: buildChannelDefaults({
        channelSlug,
        channelName: channel.channel.name,
        displayTitle,
        openingIds: [],
        unsupportedOpenings: channel.unsupported_openings ?? [],
        excludeOpenings: Array.from(openingsSelectedForAnotherProduct),
        isFree,
        vonqDetails: {
          productId: channel.product_id,
          title: channel.title,
          channelId: channel.channel.id,
          channelName: channel.channel.name,
          logoSquareUrl: channel.channel.logo_square_url,
          logoRectangleUrl: channel.channel.logo_rectangle_url,
          budget: channel.vonq_price[0]?.amount ?? 0,
          duration: channel.duration.period,
          channelType: channel.channel.type,
          processingTime: channel.time_to_process.period,
        },
      }),
    });
  };

  return (
    <CardLayout
      channelKey={formKey}
      channelSlug={channelSlug}
      icon={
        <ChannelIcon
          channelSlug={channel.channel.slug}
          channelName={channel.channel.name}
          logoSquareUrl={channel.channel.logo_square_url}
          logoRectangleUrl={channel.channel.logo_rectangle_url}
          variant="large"
          showNameLabel={false}
        />
      }
      displayTitle={displayTitle}
      recommendedDuration={channel.duration.period}
      recommendedBudget={channel.vonq_price[0]?.amount}
      channelType={channelType}
      description={description}
      processingTime={`${channel.time_to_process.period} ${channel.time_to_process.range}`}
      locations={channel.locations?.flatMap(location =>
        location.canonical_name ? [location.canonical_name] : [],
      )}
      industries={channel.industries?.map(industry => industry.name)}
      infoSheet={{
        icon: (
          <ChannelIcon
            channelSlug={channel.channel.slug}
            channelName={channel.channel.name}
            logoSquareUrl={channel.channel.logo_square_url}
            logoRectangleUrl={channel.channel.logo_rectangle_url}
            variant="large"
            size="lg"
            showNameLabel={false}
          />
        ),
        description: rawDescription,
      }}
      disabled={
        allOpeningsUnsupported || isAllOpeningsSelectedForAnotherProduct
      }
      onSelect={handleSelect}
      isFree={isFree}
      openingsSelectedForAnotherProduct={openingsSelectedForAnotherProduct}
    />
  );
}
